import './style.modules.scss'

import {
  Button,
  ButtonVariant,
  Checkbox,
  MaterialIcon,
  MaterialIconSize
} from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useEffect, useMemo, useState } from 'react'

export type ChildItem = {
  id: string
  label: string
  checked?: boolean
}

export type ParentItem = {
  id: string
  label: string
  subItems?: ChildItem[]
  defaultExpanded?: boolean
  expanded?: boolean
}

export type FilterPanelProps = {
  data: ParentItem[]
  title: string
  className?: string
  onChange?: (data: ParentItem[]) => void
  handleHideFilters?: (hasFilter: boolean, filterData: ParentItem[]) => void
  isSidePanelFilter?: boolean
  testId?: string
}

type FilterStateItem = {
  id: string
  label: string
  expanded: boolean
  subItems: {
    id: string
    label: string
    checked: boolean
  }[]
}

export const FilterPanel = (props: FilterPanelProps) => {
  const {
    data,
    title,
    className,
    onChange,
    handleHideFilters,
    isSidePanelFilter,
    testId
  } = props

  const [showFilters, setShowFilters] = useState(true)
  const [filters, setFilters] = useState<FilterStateItem[]>(
    data.map(parent => ({
      id: parent.id,
      label: parent.label,
      expanded: parent.defaultExpanded ?? parent.expanded ?? true,
      subItems: (parent.subItems ?? []).map(child => ({
        id: child.id,
        label: child.label,
        checked: child.checked ?? false
      }))
    }))
  )

  const atb = useMemo(
    () => new AnalyticsTagBuilder().setDefaultName('resource-center-results'),
    []
  )

  const updateState = (updated: FilterStateItem[]) => {
    setFilters(updated)
    onChange?.(mapToParentItems(updated))
  }

  useEffect(() => {
    const mapped = data.map(parent => ({
      id: parent.id,
      label: parent.label,
      expanded: parent.defaultExpanded ?? parent.expanded ?? true,
      subItems: (parent.subItems ?? []).map(child => ({
        id: child.id,
        label: child.label,
        checked: child.checked ?? false
      }))
    }))

    setFilters(mapped)
  }, [data])

  const mapToParentItems = (items: FilterStateItem[]): ParentItem[] => {
    return items.map(parent => ({
      id: parent.id,
      label: parent.label,
      defaultExpanded: parent.expanded,
      subItems: parent.subItems.map(child => ({
        id: child.id,
        label: child.label,
        checked: child.checked
      }))
    }))
  }

  const isParentChecked = (item: FilterStateItem) => {
    return item.subItems.length > 0 && item.subItems.every(c => c.checked)
  }

  const isIndeterminate = (item: FilterStateItem) => {
    const checkedCount = item.subItems.filter(c => c.checked).length
    return checkedCount > 0 && checkedCount < item.subItems.length
  }

  const toggleExpand = (parentId: string) => {
    updateState(
      filters.map(item =>
        item.id === parentId ? { ...item, expanded: !item.expanded } : item
      )
    )
  }

  const toggleParent = (parentId: string) => {
    updateState(
      filters.map(item => {
        if (item.id !== parentId) return item

        const shouldCheck = !isParentChecked(item)

        return {
          ...item,
          expanded: shouldCheck ? true : item.expanded,
          subItems: item.subItems.map(child => ({
            ...child,
            checked: shouldCheck
          }))
        }
      })
    )
  }

  const toggleChild = (parentId: string, childId: string) => {
    updateState(
      filters.map(item => {
        if (item.id !== parentId) return item

        return {
          ...item,
          subItems: item.subItems.map(child =>
            child.id === childId ? { ...child, checked: !child.checked } : child
          )
        }
      })
    )
  }

  const onHideFilters = () => {
    handleHideFilters?.(false, mapToParentItems(filters))
  }

  const onShowFilters = () => {
    setShowFilters(true)
  }

  return (
    <div className={`FilterPanel ${className}`}>
      <div
        className={`FilterPanel-header ${
          !isSidePanelFilter ? 'u-flex u-marginBottom24' : 'u-marginBottom8'
        } u-flexAlignItemsCenter u-flexJustifyBetween`}
      >
        {showFilters && !isSidePanelFilter && (
          <>
            <h3 className="FilterPanel-title">{title}</h3>
            <Button
              variant={ButtonVariant.LINK}
              className="FilterPanel-hideFilters u-flex"
              onClick={onHideFilters}
              {...atb.dataAnalyticAttribute('hide-filters')}
            >
              <MaterialIcon
                icon={AmpcoreMaterialIconId.TUNE}
                className="u-textColorNeutral75 u-marginRight8"
                size={MaterialIconSize.SIZE_20}
              />
              <u>Hide filters</u>
            </Button>
          </>
        )}
        {!showFilters && !isSidePanelFilter && (
          <Button
            variant={ButtonVariant.LINK}
            className="FilterPanel-showFilters u-flex"
            onClick={onShowFilters}
          >
            <MaterialIcon
              icon={AmpcoreMaterialIconId.TUNE}
              className="u-textColorNeutral u-marginRight8"
              size={MaterialIconSize.SIZE_20}
            />
            <u>Show filters</u>
          </Button>
        )}
      </div>
      {showFilters && (
        <>
          {filters.map(parent => (
            <div
              key={parent.id}
              className="FilterPanel-filterCategory u-flex u-flexAlignItemsStart u-flexJustifyBetween"
            >
              <div className="FilterPanel-filters">
                <div
                  className="u-marginBottom16"
                  data-testid={testId ? `${testId}-checkbox` : undefined}
                >
                  <Checkbox
                    id={`parent-${parent.id}`}
                    name={`parent-${parent.id}`}
                    value={parent.id}
                    label={parent.label}
                    checked={isParentChecked(parent)}
                    indeterminate={isIndeterminate(parent)}
                    onChange={() => toggleParent(parent.id)}
                    testId={
                      testId ? `${testId}-parent-${parent.id}` : undefined
                    }
                  />
                </div>

                {parent.expanded && parent.subItems && (
                  <div className="u-marginTop8 u-paddingLeft24">
                    {parent.subItems.map(child => (
                      <div
                        key={child.id}
                        className="u-marginVert16 u-marginLeft8"
                      >
                        <Checkbox
                          id={`child-${parent.id}-${child.id}`}
                          name={`child-${parent.id}-${child.id}`}
                          value={child.id}
                          label={child.label}
                          checked={child.checked}
                          onChange={() => toggleChild(parent.id, child.id)}
                          testId={
                            testId ? `${testId}-child-${child.id}` : undefined
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                className="FilterPanel-expandButton"
                onClick={() => toggleExpand(parent.id)}
                data-testid={
                  testId ? `${testId}-toggle-${parent.id}` : undefined
                }
              >
                {parent.expanded ? (
                  <MaterialIcon
                    icon={AmpcoreMaterialIconId.EXPAND_LESS}
                    className="u-textColorActionBlue"
                    aria-label="Expand Less"
                    size={MaterialIconSize.DEFAULT}
                  />
                ) : (
                  <MaterialIcon
                    icon={AmpcoreMaterialIconId.EXPAND_MORE}
                    className="u-textColorActionBlue"
                    aria-label="Expand More"
                    size={MaterialIconSize.DEFAULT}
                  />
                )}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
